import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { App, Card, Empty, Space, Table, Select } from "antd";
import theme from "../../themes";
import { pageShellCss, pageTitleCss, tableScrollCss } from "../../styles/responsive";
import { getCalls } from "../../services/call.service";
import { CallDirection, ICall, ICallListResponse } from "../../models/call.model";
import ToastMessage from "../../utils/toastMessage";
import { useAppDispatch } from "../../store";
import { getAgentsThunk } from "../../store/actions/agent.action";
import { IAgent } from "../../models/agent.model";

const statusColors: Record<string, { text: string; background: string }> = {
  "In progress": { text: theme.colors.blue1, background: theme.colors.blue2 },
  Completed: { text: theme.colors.green1, background: theme.colors.green2 },
  "Transfer to Live Agent": {
    text: theme.colors.yellow2,
    background: theme.colors.yellow3,
  },
};

const mapStatusToLabel = (raw?: string): string => {
  if (!raw) return "-";
  const value = raw.toLowerCase();

  if (value === "completed" || value === "answered") return "Completed";
  if (
    value === "ringing" ||
    value === "in-progress" ||
    value === "initiated" ||
    value === "busy"
  )
    return "In progress";
  if (value === "failed" || value === "canceled" || value === "no-answer") {
    return "Failed";
  }
  if (value.includes("transfer")) return "Transfer to Live Agent";

  // Fallback: convert kebab / snake case to nicely capitalized words
  return raw
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatCallTime = (iso?: string): string => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const { Option } = Select;

const Calls: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [calls, setCalls] = useState<ICall[]>([]);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>();
  const campaignId = searchParams.get("campaign_id") || undefined;
  const direction = ((searchParams.get("direction") as CallDirection) || "inbound") as CallDirection;

  const normalizedCalls = useMemo(() => {
    return calls.map((c) => ({
      ...c,
      _rowKey: c._id || c.sid,
      inboundAgent: c.agent_id?.agent_name || t("inbound.calls.unknownAgent"),
      fromNumber: c.from,
      callTime: c.start_time,
      displayStatus: mapStatusToLabel(c.status as string | undefined),
    }));
  }, [calls, t]);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const res = await getCalls({
        page: currentPage,
        limit: pageSize,
        campaign_id: campaignId,
        direction,
        agent_id: selectedAgentId,
      });

      const root = res.data as { data: ICallListResponse };
      const data = root.data;
      const list: ICall[] = data?.calls ?? [];
      const pagination = data.pagination;

      const total = pagination?.totalItems ?? data.count ?? 0;

      setCalls(list);
      setTotalItems(total || 0);
    } catch (e) {
      console.error(e);
      toast.error(t("inbound.calls.failedToLoad"), 5);
      setCalls([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await dispatch(
        getAgentsThunk({
          limit: 999,
          page: 1,
          keyword: "",
          direction: "inbound",
        })
      ).unwrap();
      setAgents(res.data.agents || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, campaignId, direction, selectedAgentId]);

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: t("inbound.calls.inboundAgent"),
      dataIndex: "inboundAgent",
      key: "inboundAgent",
      width: "30%",
      render: (text: string) => <PrimaryText>{text}</PrimaryText>,
    },
    {
      title: t("inbound.calls.fromNumber"),
      dataIndex: "fromNumber",
      key: "fromNumber",
      width: "25%",
      render: (text: string) => <PrimaryText>{text}</PrimaryText>,
    },
    {
      title: t("inbound.calls.status"),
      dataIndex: "displayStatus",
      key: "displayStatus",
      width: "20%",
      render: (status: string) => {
        const colors =
          statusColors[status] || { text: theme.colors.grey1, background: theme.colors.grey5 };
        return (
          <StatusBadge $text={colors.text} $bg={colors.background}>
            {status || "-"}
          </StatusBadge>
        );
      },
    },
    {
      title: t("inbound.calls.callTime"),
      dataIndex: "callTime",
      key: "callTime",
      width: "25%",
      render: (time: string) => <span>{formatCallTime(time)}</span>,
    },
  ];

  return (
    <FlexRow>
      <MainContent>
        <Title>{t("inbound.callsTitle")}</Title>

        <CardWrapper>
          <Space
            style={{
              marginBottom: 16,
              width: "100%",
              display: "flex",
            }}
          >
            <Select
              allowClear
              placeholder={t("inbound.agent")}
              value={selectedAgentId}
              style={{ width: 220, maxWidth: "100%" }}
              size="large"
              onChange={(value) => {
                setSelectedAgentId(value || undefined);
                setCurrentPage(1);
              }}
            >
              {agents.map((agent) => (
                <Option key={agent._id} value={agent._id}>
                  {agent.agent_name}
                </Option>
              ))}
            </Select>
          </Space>

          <StyledTable
            dataSource={normalizedCalls.map((c) => ({ key: (c as any)._rowKey, ...c }))}
            columns={columns}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalItems,
              position: ["bottomCenter"],
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            rowKey="key"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <EmptyStateTitle>{t("inbound.calls.noCalls")}</EmptyStateTitle>
                  }
                />
              ),
            }}
          />
        </CardWrapper>
      </MainContent>
    </FlexRow>
  );
};

const FlexRow = styled.div`
  display: flex;
  min-height: 100%;
  background-color: ${theme.colors.grey3};
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  gap: 32px;
  display: flex;
  flex-direction: column;
  ${pageShellCss}
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  ${pageTitleCss}
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  ${tableScrollCss}
`;

const PrimaryText = styled.span`
  color: ${theme.colors.blue1};
  font-weight: 500;
`;

const StatusBadge = styled.span<{ $text: string; $bg: string }>`
  background: ${({ $bg }) => $bg};
  color: ${({ $text }) => $text};
  border-radius: 12px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
    height: 64px;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

export default Calls;

